/**
 * Purpose:
 *   Wires the reference product card's colour-swatch row (the <vv-ec-card>
 *   custom element). The row is hybrid (Contract 3/5): sibling chips are <a>
 *   links that NAVIGATE to another colour's product page; in-card swatches are
 *   <button>s that SWAP the card's main image to that colour's variant preview.
 *   This module discriminates on tag: anchors navigate natively, buttons swap.
 *   Additionally (Contract 4): HOVERING or FOCUSING any swatch (anchor OR
 *   button) previews that colour's image in the card's main image; mouse-leave
 *   / blur reverts to the selected colour (if a button was clicked) or the
 *   original image. Hover scaling is CSS-only.
 *
 * Public API:
 *   Registers the `vv-ec-card` custom element. No exported symbols; behaviour
 *   is attached via delegated click + hover (mouseover/mouseout) + focus
 *   (focusin/focusout) handlers on the element. Markup contract:
 *     - swatches carry class `.vv-ec-swatch` (both <a> siblings and <button>s)
 *     - sibling chips are (or are inside) an `<a>` and carry `--sibling`
 *     - every swatch carries `data-image` — the preview/swap source (600px for
 *       both in-card buttons and sibling chips; empty -> hover/swap no-op)
 *     - the swappable image is `.vv-ec-card__img`
 *
 * Upstream dependencies:
 *   - DOM only (HTMLElement, customElements). No external libraries.
 *   - Rendered markup from snippets/vv-reference-card.liquid and
 *     snippets/vv-card-siblings.liquid.
 *
 * Downstream consumers:
 *   - Enqueued by sections that render vv-reference-card: search-results.liquid,
 *     vv-recently-viewed.liquid, and (this change) main-collection.liquid.
 *   - The drawer recs (header-actions.liquid) render the card but hide the
 *     swatch row and do NOT enqueue this script.
 *
 * Failure modes:
 *   - Pointer/focus outside a `.vv-ec-swatch` is ignored (closest() null).
 *   - Events not contained by this element are ignored (`this.contains`).
 *   - Anchor (sibling) clicks return early: no preventDefault, no swap, no
 *     selection toggle — the browser navigates.
 *   - Button (in-card) clicks with no/blank data-image toggle selection but
 *     skip the swap. The placeholder image is a <div>, not an <img>; the
 *     `img instanceof HTMLImageElement` guard skips it safely.
 *   - Hover/focus with empty `data-image` is a no-op (preview skipped).
 *   - The original image src is cached lazily into `data-vv-original-src` as the
 *     FIRST mutation on EVERY path (hover AND click), before any srcset removal
 *     or src write, so revert always restores the genuine original.
 *   - mouseout/focusout to an adjacent swatch in the same card does NOT revert
 *     (avoids flicker-to-baseline when sweeping across the row).
 *   - srcset MUST be removed before setting src, else the browser keeps the
 *     responsive source and the swap no-ops. srcset is NOT restored on revert
 *     (intentional parity with click; fixed object-fit:cover box).
 *   - Idempotent registration guard prevents double-define.
 *
 * Performance:
 *   - One delegated listener per event type per element; O(n) selection toggle
 *     over the row's buttons only on click. Hover/focus is O(1). Negligible.
 */
if (!customElements.get('vv-ec-card')) {
  customElements.define(
    'vv-ec-card',
    class VvEcCard extends HTMLElement {
      static #SWATCH_SELECTOR = '.vv-ec-swatch';

      connectedCallback() {
        this.addEventListener('click', this.#onClick);
        // Hover + focus preview (Contract 4). mouseover/mouseout bubble (unlike
        // mouseenter/leave) so a single delegated pair covers every swatch;
        // focusin/focusout give the same preview to keyboard users.
        this.addEventListener('mouseover', this.#onPreview);
        this.addEventListener('focusin', this.#onPreview);
        this.addEventListener('mouseout', this.#onRevert);
        this.addEventListener('focusout', this.#onRevert);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.#onClick);
        this.removeEventListener('mouseover', this.#onPreview);
        this.removeEventListener('focusin', this.#onPreview);
        this.removeEventListener('mouseout', this.#onRevert);
        this.removeEventListener('focusout', this.#onRevert);
      }

      #image() {
        return this.querySelector('.vv-ec-card__img');
      }

      #onClick = (event) => {
        const swatch = event.target.closest(VvEcCard.#SWATCH_SELECTOR);
        if (!swatch || !this.contains(swatch)) return;

        // Cache-first (Major 2): capture the genuine original BEFORE any src
        // mutation, even on the anchor path, so a later revert is never stuck
        // on a preview state.
        const img = this.#image();
        if (img instanceof HTMLImageElement && !img.dataset.vvOriginalSrc) {
          img.dataset.vvOriginalSrc = img.currentSrc || img.src;
        }

        // Sibling chips are (or are inside) an <a>: let the browser navigate.
        // No preventDefault, no image swap, no selection toggle.
        if (swatch.closest('a')) return;

        // In-card <button> swatch: toggle selection among the buttons only
        // (anchors are excluded so a navigated-away colour is never "selected"),
        // then swap the card image to the 600px variant preview.
        for (const sibling of this.querySelectorAll('.vv-ec-swatch:not(a)')) {
          sibling.classList.toggle('is-selected', sibling === swatch);
        }

        const src = swatch.dataset.image;
        if (src && img instanceof HTMLImageElement) {
          img.removeAttribute('srcset');
          img.src = src;
        }
      };

      #onPreview = (event) => {
        const swatch = event.target.closest(VvEcCard.#SWATCH_SELECTOR);
        if (!swatch || !this.contains(swatch)) return;

        const img = this.#image();
        if (!(img instanceof HTMLImageElement)) return;

        // Cache-first (Major 2): FIRST statement that touches the image, before
        // the empty-data-image early-return and before any srcset/src write.
        if (!img.dataset.vvOriginalSrc) {
          img.dataset.vvOriginalSrc = img.currentSrc || img.src;
        }

        const src = swatch.dataset.image;
        if (!src) return;
        if (img.src === src) return;

        img.removeAttribute('srcset');
        img.src = src;
      };

      #onRevert = (event) => {
        const swatch = event.target.closest(VvEcCard.#SWATCH_SELECTOR);
        if (!swatch || !this.contains(swatch)) return;

        const img = this.#image();
        if (!(img instanceof HTMLImageElement)) return;

        // Adjacent-swatch guard: moving onto another swatch in the same card
        // means a fresh preview is about to fire — do NOT revert in between, or
        // the image flickers back to baseline mid-sweep.
        const to = event.relatedTarget;
        if (to && to.closest && to.closest(VvEcCard.#SWATCH_SELECTOR) && this.contains(to)) return;

        // Revert baseline: the clicked-selected colour's image (buttons only),
        // else the genuine original. Anchors are excluded from the selected
        // lookup so a hovered sibling never becomes the revert target.
        const selected = this.querySelector('.vv-ec-swatch.is-selected:not(a)');
        const revertSrc = (selected && selected.dataset.image) || img.dataset.vvOriginalSrc;
        if (!revertSrc || img.src === revertSrc) return;

        img.removeAttribute('srcset');
        img.src = revertSrc;
      };
    }
  );
}
